
import Vue from "vue";
import Breadcrump from "@/components/common/Breadcrump.vue";
import request from "@/utils/request";

interface OTAItem {
  id: number;
  version: string;
  updateTitle: string;
  isActive: boolean;
  forceUpdate: boolean;
  createdAt: string;
}

interface ApiDeviceItem {
  deviceId: number;
  deviceName: string;
  complexId: number;
  complexName: string;
  isAssigned: boolean;
  type?: string | null;
}

interface DeviceItem {
  deviceId: number;
  deviceName: string;
  type?: string | null;
}

interface ComplexItem {
  complexId: number;
  complexName: string;
  devices: DeviceItem[];
}

export default Vue.extend({
  components: { Breadcrump },

  data() {
    return {
      loading: false,
      otaList: [] as OTAItem[],
      abortController: null as AbortController | null,
      downloadingId: null as number | null,
      assignDialog: false,
      assignLoading: false,
      selectedOtaId: null as number | null,
      currentOtaVersion: "",
      selectedTags: [] as string[],
      allDevices: [] as ComplexItem[],
      selectedDevices: [] as number[],
      deviceSearch: "",

      crumbs: [
        { text: "خانه", href: "/", disabled: false },
        { text: "نسخه‌های دستگاه", href: "", disabled: true }
      ],

      headers: [
        { text: "عنوان", value: "updateTitle" },
        { text: "نسخه", value: "version" },
        { text: "وضعیت", value: "isActive" },
        { text: "نوع آپدیت", value: "forceUpdate" },
        { text: "تاریخ ایجاد", value: "createdAt" },
        { text: "عملیات", value: "actions", sortable: false }
      ]
    };
  },

  computed: {
    allTags(): string[] {
      const tags = new Set<string>();
      this.allDevices.forEach(c => {
        c.devices.forEach(d => {
          if (d.type) tags.add(d.type);
        });
      });
      return Array.from(tags);
    },
    filteredComplexes(): ComplexItem[] {
      const t = this.deviceSearch.toLowerCase();
      let filtered = this.allDevices
        .map(c => ({
          ...c,
          devices: c.devices.filter(
            d =>
              d.deviceName.toLowerCase().includes(t) ||
              c.complexName.toLowerCase().includes(t)
          )
        }))
        .filter(c => c.devices.length);
      if (this.selectedTags.length > 0)
        filtered = filtered
          .map(c => ({
            ...c,
            devices: c.devices.filter(d =>
              this.selectedTags.includes(d.type ?? "")
            )
          }))
          .filter(c => c.devices.length);
      return filtered;
    }
  },

  methods: {
    async downloadOfflineUpdate(item: OTAItem) {
      try {
        this.downloadingId = item.id;
        if (this.abortController) {
          this.abortController.abort();
          this.abortController = null;
        }
        this.abortController = new AbortController();
        const response = await request.get(
          `/DeviceOTAList/offline-update/${item.id}`,
          {
            signal: this.abortController.signal,
            responseType: "blob"
          }
        );
        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/zip" })
        );

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `offlineUpdate_v${item.version}_${new Date()
            .toISOString()
            .slice(0, 10)}.zip`
        );

        document.body.appendChild(link);
        link.click();

        // پاکسازی
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.downloadingId = null;
      } catch {
        this.downloadingId = null;
      } finally {
        this.abortController = null;
      }
    },
    selectAllFilteredDevices() {
      const filteredIds =
        this.deviceSearch || this.selectedTags.length > 0
          ? this.filteredComplexes.flatMap(c => c.devices.map(d => d.deviceId))
          : [];

      this.selectedDevices = Array.from(
        new Set([...this.selectedDevices, ...filteredIds])
      );
    },
    persianDate(d: string) {
      return new Date(d).toLocaleDateString("fa-IR");
    },

    async loadData() {
      this.loading = true;
      const res = await request.get("/DeviceOTAList/versions");
      this.otaList = res.data.result;
      this.loading = false;
    },

    editItem(item: OTAItem) {
      this.$router.push({
        name: "DeviceOTAFormEdit",
        params: { id: String(item.id) }
      });
    },

    async openAssignModal(otaId: number) {
      this.selectedOtaId = otaId;
      const ota = this.otaList.find(o => o.id === otaId);
      this.currentOtaVersion = ota?.version || "";
      this.assignDialog = true;
      await this.loadDevicesForOta(otaId);
    },

    closeAssignModal() {
      this.assignDialog = false;
      this.selectedDevices = [];
      this.allDevices = [];
    },

    async loadDevicesForOta(otaId: number) {
      const res = await request.get(`/DeviceOTAList/${otaId}/get-devices`);
      const list: ApiDeviceItem[] = res.data.result;

      const map = new Map<number, ComplexItem>();
      const assigned: number[] = [];

      list.forEach(i => {
        if (!map.has(i.complexId)) {
          map.set(i.complexId, {
            complexId: i.complexId,
            complexName: i.complexName || "نامشخص",
            devices: []
          });
        }
        const complex = map.get(i.complexId);
        if (!complex) return;

        complex.devices.push({
          deviceId: i.deviceId,
          deviceName: i.deviceName,
          type: i.type
        });

        if (i.isAssigned) assigned.push(i.deviceId);
      });

      this.allDevices = Array.from(map.values());
      this.selectedDevices = assigned;
    },

    getDeviceSimpleName(id: number) {
      for (const c of this.allDevices) {
        const d = c.devices.find(x => x.deviceId === id);
        if (d) return d.deviceName;
      }
      return "نامشخص";
    },

    getDeviceComplexName(id: number) {
      for (const c of this.allDevices) {
        const d = c.devices.find(x => x.deviceId === id);
        if (d) return c.complexName;
      }
      return "";
    },
    getDeviceType(id: number) {
      for (const c of this.allDevices) {
        const d = c.devices.find(x => x.deviceId === id);
        if (d) return d.type;
      }
      return null;
    },
    removeDevice(id: number) {
      this.selectedDevices = this.selectedDevices.filter(d => d !== id);
    },

    async removeUpdated() {
      if (!this.selectedOtaId) return;

      await request.delete(
        `/DeviceOTAList/${this.selectedOtaId}/remove-updated`
      );
      await this.loadDevicesForOta(this.selectedOtaId);
    },

    async submitAssignment() {
      if (!this.selectedOtaId) return;
      this.assignLoading = true;

      await request.post("/DeviceOTAList/set-devices", {
        otaId: this.selectedOtaId,
        deviceIds: this.selectedDevices
      });

      this.assignLoading = false;
      this.closeAssignModal();
    }
  },

  created() {
    this.loadData();
  },
  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
});
